//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';

import Cuid from '@models/Cuid';
import DraftId from '@models/DraftId';
import { PostDetailsPostType } from '@models/PostDetailsPostType';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';

import { RoutableStateName } from 'app/top-level-states';

export enum TikTokVideoAutomaticValidationError {
  CaptionTooLong = 'CaptionTooLong',
  InvalidThumbnailOffset = 'InvalidThumbnailOffset',
  InvalidVideoCoverImage = 'InvalidVideoCoverImage',
  InvalidVideoDuration = 'InvalidVideoDuration',
  InvalidVideoDimensions = 'InvalidVideoDimensions',
  NoMedia = 'NoMedia',
  NoScheduledTime = 'NoScheduledTime',
  ScheduledTimeInPast = 'ScheduledTimeInPast',
}

export interface IStaticPostDetails {
  draftId?: DraftId;
  postableId?: PostableId;
  postType: PostDetailsPostType.TikTokVideoAutomatic;
  status?: PostableStatus;
}

export interface IComparableTikTokVideoAutomaticPostDetails {
  caption: string;
  cuid: Cuid | undefined;
  disableComment: boolean;
  disableDuet: boolean;
  disableStitch: boolean;
  scheduledTime?: Date;
  thumbnailOffsetInMs?: number;
}

export interface ITikTokVideoAutomaticPostDetailsInfoModal {
  isOpen: boolean;
  contents: string;
  primaryButtonText: string;
  onCloseCallback: () => void;
  title: string;
}

export interface ITikTokVideoAutomaticPostDetailsState
  extends IStaticPostDetails {
  postType: PostDetailsPostType.TikTokVideoAutomatic;

  initialPostDetails: IComparableTikTokVideoAutomaticPostDetails;
  currentPostDetails: IComparableTikTokVideoAutomaticPostDetails;
  validationErrors: PojoSet<TikTokVideoAutomaticValidationError>;
  schedulingEntryPoint: RoutableStateName | undefined;

  postDetailsInfoModal: ITikTokVideoAutomaticPostDetailsInfoModal;
  isTikTokThumbnailSelectionModalOpen: boolean;

  errors: {
    deletePostError: string | undefined;
    fetchCuidsError: string | undefined;
    saveDraftPostError: string | undefined;
    schedulePostError: string | undefined;
    setInitialPostDetailsError: string | undefined;
  };
  flags: {
    hasSetInitialPostDetails: boolean;
    isDeletingPost: boolean;
    isFetchingCuid: boolean;
    isSavingDraftPost: boolean;
    isSchedulingPost: boolean;
  };
}

// https://business-api.tiktok.com/portal/docs?id=1762228496095234
export const CAPTION_LENGTH_LIMIT = 2200;
export const TIKTOK_MIN_DIMENSION_PX = 360;
export const TIKTOK_MIN_VIDEO_DURATION_MILLIS = 3000; // 3 seconds
export const TIKTOK_MAX_VIDEO_DURATION_MILLIS = 600000; // 10 minutes

export const SECONDS_TO_MILLISECONDS_CONVERSION_FACTOR = 1000;

export const fallbackComparablePostDetails: IComparableTikTokVideoAutomaticPostDetails =
  {
    caption: '',
    cuid: undefined,
    disableComment: false,
    disableDuet: false,
    disableStitch: false,
    scheduledTime: undefined,
    thumbnailOffsetInMs: undefined,
  };
